import Navbar from '../Components/Navbar';
import { Footer } from '../Components/Footer';
import { useState } from 'react';
import { useTranslation } from '../../TranslationContext';
import { Check, ChevronRight } from 'lucide-react';

interface Offer {
  id: string;
  title: string;
  price: string;
  features: string[];
}

interface Application {
  id: string;
  name: string;
  description: string;
  image: string;
}

interface PaymentFormData {
  fullName: string;
  email: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  selectedOfferId: string;
}

export const Sales = () => {
  const { t } = useTranslation();
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [paymentStatus, setPaymentStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  
  const [paymentFormData, setPaymentFormData] = useState<PaymentFormData>({
    fullName: '',
    email: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    selectedOfferId: ''
  });

  const offers: Offer[] = [
    {
      id: "starter",
      title: t('starter_plan'),
      price: t('starter_price'),
      features: [
        t('multi_factor_authentication'),
        t('up_to_1000_users'),
        t('email_support'),
        t('basic_api_integration')
      ]
    },
    {
      id: "professional",
      title: t('professional_plan'),
      price: t('professional_price'),
      features: [
        t('everything_from_starter'),
        t('up_to_10000_users'),
        t('support_24_7'),
        t('sso_saml_integration'),
        t('advanced_analytics')
      ]
    },
    {
      id: "enterprise",
      title: t('enterprise_plan'),
      price: t('enterprise_price'),
      features: [
        t('everything_from_professional'),
        t('unlimited_users'),
        t('dedicated_support'),
        t('on_premises_deployment'),
        t('full_customization')
      ]
    }
  ];

  const applications: Application[] = [
    {
      id: "crm",
      name: t('crm_pro'),
      description: t('crm_pro_description'),
      image: "/crm.jpg"
    },
    {
      id: "erp",
      name: t('erp_suite'),
      description: t('erp_suite_description'),
      image: "/erp.jpg"
    },
    {
      id: "ecommerce",
      name: t('ecommerce_boost'),
      description: t('ecommerce_boost_description'),
      image: "/ecommerce.jpg"
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPaymentFormData((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSelectOffer = (offer: Offer) => {
    setSelectedOffer(offer);
    setPaymentFormData((prev) => ({
      ...prev,
      selectedOfferId: offer.id
    }));
    
    setTimeout(() => {
      document.getElementById('payment-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setPaymentStatus({ type: null, message: '' });
    
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      setPaymentStatus({
        type: 'success',
        message: t('payment_success')
      });
      
      setTimeout(() => {
        setSelectedOffer(null);
        setPaymentFormData({
          fullName: '',
          email: '',
          cardNumber: '',
          expiryDate: '',
          cvv: '',
          selectedOfferId: ''
        });
      }, 3000);
      
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus({
        type: 'error',
        message: t('payment_failed')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero section with background */}
      <div className="bg-gradient-to-b from-[#2C3E50] to-[#4A5568] py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('/background1.jpg')" }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-white">
              {t('our_solutions')}
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              {t('solutions_description')}
            </p>
            <div className="h-1 w-32 mx-auto bg-[#F3DAB3] rounded-full mt-6"></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {offers.map((offer, index) => (
            <div 
              key={index} 
              className={`rounded-xl shadow-md p-8 border transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2 ${
                index === 1 ? 'bg-gradient-to-br from-[#F3DAB3] to-[#F8E6CA] border-[#F3DAB3]/30' : 'bg-white border-gray-200'
              }`}
            >
              <h2 className="text-2xl font-bold mb-4 text-[#2C3E50]">{offer.title}</h2>
              <p className="text-3xl font-bold mb-6 text-[#2C3E50]">{offer.price}</p>
              <ul className="space-y-3 mb-8">
                {offer.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-[#2C3E50] group">
                    <div className={`p-1 rounded-full mr-3 shadow-sm transition-colors duration-300 ${
                      index === 1 ? 'bg-white group-hover:bg-[#2C3E50]' : 'bg-[#F3DAB3] group-hover:bg-[#2C3E50]'
                    }`}>
                      <Check className="h-4 w-4 text-[#2C3E50] group-hover:text-white" />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => handleSelectOffer(offer)} 
                className={`w-full py-3 px-4 rounded-lg text-white transition-all duration-300 active:scale-95 ${
                  index === 1 
                    ? 'bg-[#2C3E50] hover:bg-[#1A2533]' 
                    : 'bg-[#2C3E50] hover:bg-[#1A2533]'
                }`}
              >
                {t('subscribe')}
              </button>
            </div>
          ))}
        </div>

        {/* Payment form section */}
        {selectedOffer && (
          <div id="payment-form" className="mt-12 p-8 bg-white rounded-xl shadow-lg border border-gray-200 max-w-2xl mx-auto mb-16 transform transition-all duration-500 hover:shadow-xl">
            <h2 className="text-2xl font-bold mb-6 text-[#2C3E50]">{t('payment_for')}</h2>
            
            {paymentStatus.type ? (
              <div 
                className={`p-4 mb-6 rounded-lg ${
                  paymentStatus.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}
              >
                {paymentStatus.message}
              </div>
            ) : (
              <form onSubmit={handlePaymentSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-[#2C3E50] mb-1">{t('full_name')}</label>
                  <input
                    type="text"
                    id="fullName"
                    value={paymentFormData.fullName}
                    onChange={handleInputChange}
                    required
                    className="w-full h-12 rounded-lg border-gray-300 shadow-sm focus:border-[#F3DAB3] focus:ring focus:ring-[#F3DAB3] focus:ring-opacity-50 bg-white px-3"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#2C3E50] mb-1">{t('email')}</label>
                  <input
                    type="email"
                    id="email"
                    value={paymentFormData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full h-12 rounded-lg border-gray-300 shadow-sm focus:border-[#F3DAB3] focus:ring focus:ring-[#F3DAB3] focus:ring-opacity-50 bg-white px-3"
                  />
                </div>
                
                <div>
                  <label htmlFor="cardNumber" className="block text-sm font-medium text-[#2C3E50] mb-1">{t('card_number')}</label>
                  <input
                    type="text"
                    id="cardNumber"
                    value={paymentFormData.cardNumber}
                    onChange={handleInputChange}
                    placeholder={t('card_number_placeholder')}
                    required
                    className="w-full h-12 rounded-lg border-gray-300 shadow-sm focus:border-[#F3DAB3] focus:ring focus:ring-[#F3DAB3] focus:ring-opacity-50 bg-white px-3"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="expiryDate" className="block text-sm font-medium text-[#2C3E50] mb-1">{t('expiry_date')}</label>
                    <input
                      type="text"
                      id="expiryDate"
                      value={paymentFormData.expiryDate}
                      onChange={handleInputChange}
                      placeholder={t('expiry_date_placeholder')}
                      required
                      className="w-full h-12 rounded-lg border-gray-300 shadow-sm focus:border-[#F3DAB3] focus:ring focus:ring-[#F3DAB3] focus:ring-opacity-50 bg-white px-3"
                    />
                  </div>
                  <div>
                    <label htmlFor="cvv" className="block text-sm font-medium text-[#2C3E50] mb-1">{t('cvv')}</label>
                    <input
                      type="text"
                      id="cvv"
                      value={paymentFormData.cvv}
                      onChange={handleInputChange}
                      placeholder={t('cvv_placeholder')}
                      required
                      className="w-full h-12 rounded-lg border-gray-300 shadow-sm focus:border-[#F3DAB3] focus:ring focus:ring-[#F3DAB3] focus:ring-opacity-50 bg-white px-3"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-[#2C3E50] text-white py-3 px-4 rounded-lg transition-all duration-300 hover:bg-[#1A2533] active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? t('processing') : t('pay')}
                </button>
                
                <p className="text-sm text-gray-500 text-center mt-4">
                  {t('payment_secure')}
                </p>
              </form>
            )}
          </div>
        )}

        {/* Applications section */}
        <section className="mt-16 bg-gradient-to-b from-[#DDE6F1] to-white py-16 px-8 rounded-xl">
          <h2 className="text-3xl font-bold text-[#2C3E50] mb-8 text-center">{t('our_applications')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {applications.map((app, index) => (
              <div 
                key={index} 
                className="p-6 bg-white shadow-md rounded-xl text-center transition-all duration-300 hover:shadow-xl hover:-translate-y-2 transform group"
              >
                <div className="h-48 bg-gradient-to-r from-[#2C3E50] to-[#4A5568] rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                  <img src={app.image} alt={app.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-[#2C3E50] group-hover:text-[#1A2533]">{app.name}</h3>
                <p className="text-[#2C3E50]/80 mt-2">{app.description}</p>
                <button className="mt-4 py-2 px-4 border border-[#2C3E50] text-[#2C3E50] rounded-lg hover:bg-[#2C3E50] hover:text-white transition-colors duration-300 flex items-center mx-auto">
                  {t('learn_more')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
        
        {/* Call to action */}
        <div className="mt-20 text-center p-8 bg-gradient-to-br from-[#F3DAB3] to-[#F8E6CA] rounded-xl shadow-lg transform transition-all duration-500 hover:scale-105">
          <h2 className="text-2xl font-bold text-[#2C3E50] mb-4">{t('custom_solution_title')}</h2>
          <p className="text-[#2C3E50] mb-6 max-w-2xl mx-auto">
            {t('custom_solution_description')}
          </p>
          <button className="py-3 px-6 bg-[#2C3E50] text-white rounded-lg hover:bg-[#1A2533] transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center mx-auto">
            {t('contact_our_team')}
            <ChevronRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Sales;

// import Navbar from '../Components/Navbar';
// import { Footer } from '../Components/Footer';
// import { useState } from 'react';
// import { useTranslation } from '../../TranslationContext';

// interface Offer {
//   id: string;
//   title: string;
//   price: string;
//   features: string[];
// }

// interface Application {
//   id: string;
//   name: string;
//   description: string;
//   image: string;
// }

// interface PaymentFormData {
//   fullName: string;
//   email: string;
//   cardNumber: string;
//   expiryDate: string;
//   cvv: string;
//   selectedOfferId: string;
// }

// export const Sales = () => {
//   const { t } = useTranslation();
//   const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
//   const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
//   const [paymentStatus, setPaymentStatus] = useState<{
//     type: 'success' | 'error' | null;
//     message: string;
//   }>({ type: null, message: '' });
  
//   const [paymentFormData, setPaymentFormData] = useState<PaymentFormData>({
//     fullName: '',
//     email: '',
//     cardNumber: '',
//     expiryDate: '',
//     cvv: '',
//     selectedOfferId: ''
//   });

//   const offers: Offer[] = [
//     {
//       id: "starter",
//       title: t('starter_plan'),
//       price: t('starter_price'),
//       features: [
//         t('multi_factor_authentication'),
//         t('up_to_1000_users'),
//         t('email_support'),
//         t('basic_api_integration')
//       ]
//     },
//     {
//       id: "professional",
//       title: t('professional_plan'),
//       price: t('professional_price'),
//       features: [
//         t('everything_from_starter'),
//         t('up_to_10000_users'),
//         t('support_24_7'),
//         t('sso_saml_integration'),
//         t('advanced_analytics')
//       ]
//     },
//     {
//       id: "enterprise",
//       title: t('enterprise_plan'),
//       price: t('enterprise_price'),
//       features: [
//         t('everything_from_professional'),
//         t('unlimited_users'),
//         t('dedicated_support'),
//         t('on_premises_deployment'),
//         t('full_customization')
//       ]
//     }
//   ];

//   const applications: Application[] = [
//     {
//       id: "crm",
//       name: t('crm_pro'),
//       description: t('crm_pro_description'),
//       image: "/crm.jpg"
//     },
//     {
//       id: "erp",
//       name: t('erp_suite'),
//       description: t('erp_suite_description'),
//       image: "/erp.jpg"
//     },
//     {
//       id: "ecommerce",
//       name: t('ecommerce_boost'),
//       description: t('ecommerce_boost_description'),
//       image: "/ecommerce.jpg"
//     }
//   ];

//   const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const { id, value } = e.target;
//     setPaymentFormData((prev) => ({
//       ...prev,
//       [id]: value
//     }));
//   };

//   const handleSelectOffer = (offer: Offer) => {
//     setSelectedOffer(offer);
//     setPaymentFormData((prev) => ({
//       ...prev,
//       selectedOfferId: offer.id
//     }));
    
//     setTimeout(() => {
//       document.getElementById('payment-form')?.scrollIntoView({ behavior: 'smooth' });
//     }, 100);
//   };

//   const handlePaymentSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     setIsSubmitting(true);
//     setPaymentStatus({ type: null, message: '' });
    
//     try {
//       // Simulate API call
//       await new Promise((resolve) => setTimeout(resolve, 1500));
      
//       setPaymentStatus({
//         type: 'success',
//         message: t('payment_success', )
//         // message: t('payment_success', { plan: selectedOffer?.title })
//       });
      
//       setTimeout(() => {
//         setSelectedOffer(null);
//         setPaymentFormData({
//           fullName: '',
//           email: '',
//           cardNumber: '',
//           expiryDate: '',
//           cvv: '',
//           selectedOfferId: ''
//         });
//       }, 3000);
      
//     } catch (error) {
//       console.error('Payment error:', error);
//       setPaymentStatus({
//         type: 'error',
//         message: t('payment_failed')
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   // Color definitions (single distinct colors)
//   const colors = {
//     white: "#FFFFFF",
//     gray: "#F5F7FA",
//     beigeLight: "#f6e6d1",
//     roseRed: "#d88c9a",
//     textDark: "#333333"
//   };

//   return (
//     <div className="min-h-screen bg-white">
//       <Navbar />
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//         {/* Hero section */}
//         <div className="text-center mb-16">
//           <h1 className="text-5xl md:text-6xl font-extrabold mb-4 text-textDark">
//             {t('our_solutions')}
//           </h1>
//           <p className="text-xl text-textDark max-w-3xl mx-auto">
//             {t('solutions_description')}
//           </p>
//           <div className="h-1 w-32 mx-auto bg-roseRed rounded-full mt-6"></div>
//         </div>

//         {/* Pricing cards */}
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
//           {offers.map((offer, index) => (
//             <div 
//               key={index} 
//               className="rounded-lg shadow-md p-8 border border-gray bg-white hover:shadow-lg transition-all duration-300"
//               style={{
//                 borderColor: index === 1 ? colors.roseRed : colors.gray,
//                 backgroundColor: index === 1 ? colors.beigeLight : colors.white,
//               }}
//             >
//               <h2 className="text-2xl font-bold mb-4 text-textDark">{offer.title}</h2>
//               <p className="text-3xl font-bold mb-6 text-roseRed">{offer.price}</p>
//               <ul className="space-y-3 mb-8">
//                 {offer.features.map((feature, featureIndex) => (
//                   <li key={featureIndex} className="flex items-center text-textDark">
//                     <span className="mr-2 text-roseRed">✓</span>{feature}
//                   </li>
//                 ))}
//               </ul>
//               <button 
//                 onClick={() => handleSelectOffer(offer)} 
//                 className={`w-full py-3 px-4 rounded-md text-white transition-all duration-300 active:scale-95 ${
//                   index === 1 
//                     ? 'bg-roseRed hover:bg-roseRed/90' 
//                     : 'bg-textDark hover:bg-textDark/90'
//                 }`}
//               >
//                 {t('subscribe')}
//               </button>
//             </div>
//           ))}
//         </div>

//         {/* Payment form section */}
//         {selectedOffer && (
//           <div id="payment-form" className="mt-12 p-8 bg-white rounded-lg shadow-md border border-gray max-w-2xl mx-auto mb-16">
//             <h2 className="text-2xl font-bold mb-6 text-textDark">{t('payment_for', )}</h2>
            
//             {paymentStatus.type ? (
//               <div 
//                 className={`p-4 mb-6 rounded ${
//                   paymentStatus.type === 'success' 
//                     ? 'bg-green-100 text-green-800 border border-green-200' 
//                     : 'bg-red-100 text-red-800 border border-red-200'
//                 }`}
//               >
//                 {paymentStatus.message}
//               </div>
//             ) : (
//               <form onSubmit={handlePaymentSubmit} className="space-y-6">
//                 <div>
//                   <label htmlFor="fullName" className="block text-sm font-medium text-textDark mb-1">{t('full_name')}</label>
//                   <input
//                     type="text"
//                     id="fullName"
//                     value={paymentFormData.fullName}
//                     onChange={handleInputChange}
//                     required
//                     className="w-full h-12 rounded-md border-gray shadow-sm focus:border-roseRed focus:ring-roseRed bg-white px-3"
//                   />
//                 </div>
                
//                 <div>
//                   <label htmlFor="email" className="block text-sm font-medium text-textDark mb-1">{t('email')}</label>
//                   <input
//                     type="email"
//                     id="email"
//                     value={paymentFormData.email}
//                     onChange={handleInputChange}
//                     required
//                     className="w-full h-12 rounded-md border-gray shadow-sm focus:border-roseRed focus:ring-roseRed bg-white px-3"
//                   />
//                 </div>
                
//                 <div>
//                   <label htmlFor="cardNumber" className="block text-sm font-medium text-textDark mb-1">{t('card_number')}</label>
//                   <input
//                     type="text"
//                     id="cardNumber"
//                     value={paymentFormData.cardNumber}
//                     onChange={handleInputChange}
//                     placeholder={t('card_number_placeholder')}
//                     required
//                     className="w-full h-12 rounded-md border-gray shadow-sm focus:border-roseRed focus:ring-roseRed bg-white px-3"
//                   />
//                 </div>
                
//                 <div className="grid grid-cols-2 gap-4">
//                   <div>
//                     <label htmlFor="expiryDate" className="block text-sm font-medium text-textDark mb-1">{t('expiry_date')}</label>
//                     <input
//                       type="text"
//                       id="expiryDate"
//                       value={paymentFormData.expiryDate}
//                       onChange={handleInputChange}
//                       placeholder={t('expiry_date_placeholder')}
//                       required
//                       className="w-full h-12 rounded-md border-gray shadow-sm focus:border-roseRed focus:ring-roseRed bg-white px-3"
//                     />
//                   </div>
//                   <div>
//                     <label htmlFor="cvv" className="block text-sm font-medium text-textDark mb-1">{t('cvv')}</label>
//                     <input
//                       type="text"
//                       id="cvv"
//                       value={paymentFormData.cvv}
//                       onChange={handleInputChange}
//                       placeholder={t('cvv_placeholder')}
//                       required
//                       className="w-full h-12 rounded-md border-gray shadow-sm focus:border-roseRed focus:ring-roseRed bg-white px-3"
//                     />
//                   </div>
//                 </div>
                
//                 <button
//                   type="submit"
//                   disabled={isSubmitting}
//                   className="w-full h-12 bg-roseRed text-white py-3 px-4 rounded-md transition-all duration-300 hover:bg-roseRed/90 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
//                 >
//                   {isSubmitting ? t('processing') : t('pay',)}
//                   {/* {isSubmitting ? t('processing') : t('pay', { price: selectedOffer.price })} */}
//                 </button>
                
//                 <p className="text-sm text-gray-500 text-center mt-4">
//                   {t('payment_secure')}
//                 </p>
//               </form>
//             )}
//           </div>
//         )}

//         {/* Applications section */}
//         <section className="mt-16 bg-gray py-16 px-8 rounded-lg">
//           <h2 className="text-3xl font-bold text-textDark mb-8 text-center">{t('our_applications')}</h2>
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//             {applications.map((app, index) => (
//               <div 
//                 key={index} 
//                 className="p-6 bg-white shadow-md rounded-lg text-center transition-all duration-300 hover:shadow-lg"
//               >
//                 <div className="h-48 bg-beigeLight rounded-md mb-4 flex items-center justify-center overflow-hidden">
//                   <img src={app.image} alt={app.name} className="w-full h-full object-cover" />
//                 </div>
//                 <h3 className="text-xl font-semibold text-roseRed">{app.name}</h3>
//                 <p className="text-textDark mt-2">{app.description}</p>
//                 <button className="mt-4 py-2 px-4 border border-roseRed text-roseRed rounded-md hover:bg-roseRed hover:text-white transition-colors">
//                   {t('learn_more')}
//                 </button>
//               </div>
//             ))}
//           </div>
//         </section>
        
//         {/* Call to action */}
//         <div className="mt-20 text-center p-8 bg-beigeLight rounded-lg">
//           <h2 className="text-2xl font-bold text-textDark mb-4">{t('custom_solution_title')}</h2>
//           <p className="text-textDark mb-6 max-w-2xl mx-auto">
//             {t('custom_solution_description')}
//           </p>
//           <button className="py-3 px-6 bg-roseRed text-white rounded-md hover:bg-roseRed/90 transition-all duration-300">
//             {t('contact_our_team')}
//           </button>
//         </div>
//       </div>
//       <Footer />
//     </div>
//   );
// };

// export default Sales;
